// Estado da sessão no navegador: quem está logado e qual plano vale agora.
//
// Uma loja só, fora do React, lida com useSyncExternalStore: o cabeçalho, a ficha
// (botão de monitorar) e as telas da conta leem a mesma resposta, e um login em uma
// delas atualiza todas sem recarregar a página.

import { useEffect, useSyncExternalStore } from "react";
import type { PlatformInfo, SessionPayload } from "../../shared/conta";
import { IS_STATIC_BUILD } from "./api";
import { apiLogout, apiPlatform, apiSession } from "./conta";

export type SessionState =
  | { status: "loading" }
  | { status: "anon"; payload: SessionPayload | null }
  | { status: "auth"; payload: SessionPayload & { usuario: NonNullable<SessionPayload["usuario"]> } };

let state: SessionState = { status: "loading" };
let inflight: Promise<void> | null = null;
const listeners = new Set<() => void>();

function emit(next: SessionState): void {
  state = next;
  for (const listener of listeners) listener();
}

/** Grava a resposta do servidor (login, cadastro, troca de plano) como a sessão atual. */
export function setSession(payload: SessionPayload | null): void {
  if (payload?.usuario) emit({ status: "auth", payload: payload as Extract<SessionState, { status: "auth" }>["payload"] });
  else emit({ status: "anon", payload });
}

export function refreshSession(): Promise<void> {
  if (IS_STATIC_BUILD) {
    emit({ status: "anon", payload: null });
    return Promise.resolve();
  }
  if (inflight) return inflight;
  inflight = apiSession()
    .then((payload) => setSession(payload))
    .catch(() => {
      // Sem servidor ou sem rede: trata como visitante. A busca continua funcionando.
      if (state.status === "loading") emit({ status: "anon", payload: null });
    })
    .finally(() => {
      inflight = null;
    });
  return inflight;
}

export async function logout(): Promise<void> {
  try {
    await apiLogout();
  } finally {
    emit({ status: "anon", payload: null });
  }
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useSession(): SessionState {
  const current = useSyncExternalStore(subscribe, () => state, () => state);
  useEffect(() => {
    if (state.status === "loading") void refreshSession();
  }, []);
  return current;
}

// -- informações públicas da plataforma (pagamento e e-mail configurados) -----

let platform: PlatformInfo | null = null;
let platformInflight: Promise<PlatformInfo | null> | null = null;
const platformListeners = new Set<() => void>();

export function loadPlatform(): Promise<PlatformInfo | null> {
  if (platform) return Promise.resolve(platform);
  if (IS_STATIC_BUILD) return Promise.resolve(null);
  if (!platformInflight) {
    platformInflight = apiPlatform()
      .then((info) => {
        platform = info;
        for (const listener of platformListeners) listener();
        return info;
      })
      .catch(() => null)
      .finally(() => {
        platformInflight = null;
      });
  }
  return platformInflight;
}

/** `null` enquanto carrega ou quando não há servidor (build estática). */
export function usePlatform(): PlatformInfo | null {
  const current = useSyncExternalStore(
    (listener) => {
      platformListeners.add(listener);
      return () => platformListeners.delete(listener);
    },
    () => platform,
    () => platform,
  );
  useEffect(() => {
    if (!platform) void loadPlatform();
  }, []);
  return current;
}
